import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import {
  DataTableFilter,
  FilterType,
  ProjectionKind,
  useTaxTypeDatable,
} from 'generated/resources/datatable.graphql'
import { FinanceTaxTypeFilter } from 'generated/resources/graphql'

import DataTable from 'modules/core/components/DataTable'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { countries } from 'modules/core/lib/countryList'
import i18n from 'modules/core/lib/i18n'

import { TAX_TYPE } from 'modules/finance/lib/taxTypes'

import Actions from './_Actions'

interface Props {
  showDetailTaxType: (taxTypeId: string) => void
  showEditTaxType: (taxTypeId: string) => void
  showDeleteTaxType: (taxTypeId: string) => void
  onNewTaxTypeClick: () => void
}

const TaxTypeDatatable = ({
  showDetailTaxType,
  showEditTaxType,
  showDeleteTaxType,
  onNewTaxTypeClick,
}: Props) => {
  const { debouncedSearch, search, onSearch } = useDebouncedSearch()

  const source = useTaxTypeDatable(
    [
      {
        identifier: 'name',
        title: i18n.t('finance.tax_type.datatable.fields.name'),
        kind: ProjectionKind.Path,
        path: 'name',
      },
      {
        identifier: 'type',
        title: i18n.t('finance.tax_type.datatable.fields.type'),
        kind: ProjectionKind.Custom,
        sortBy: 'type',
        render: ({ type }) => <Text.Normal size="200">{TAX_TYPE(type)}</Text.Normal>,
        template: '{{ type }}',
      },
      {
        identifier: 'countryCode',
        title: i18n.t('finance.tax_type.datatable.fields.country_code'),
        kind: ProjectionKind.Custom,
        sortBy: 'countryCode',
        render: ({ countryCode }) => (
          <Text.Normal size="200">{countryCode?.toUpperCase() || ''}</Text.Normal>
        ),
        template: '{{ countryCode }}',
      },
      {
        identifier: 'actions',
        kind: ProjectionKind.Decorative,
        width: 50,
        render: ({ id, source }) => {
          return (
            <Actions
              source={source || 'system'}
              onShowDetailClick={() => showDetailTaxType(id)}
              onShowEditClick={() => showEditTaxType(id)}
              onShowDeleteClick={() => showDeleteTaxType(id)}
            />
          )
        },
      },
    ],
    {
      variables: {
        search: debouncedSearch,
      },
    }
  )

  const filters: DataTableFilter<FinanceTaxTypeFilter>[] = [
    {
      identifier: 'type',
      title: i18n.t('finance.tax_type.datatable.fields.type'),
      path: 'type',
      type: FilterType.String,
      categories: [
        {
          kind: 'MultipleChoice',
          operation: 'in',
          options: [
            {
              label: i18n.t('finance.modals.new_tax_type.fields.type_selector.options.vat'),
              value: 'vat',
            },
            {
              label: i18n.t(
                'finance.modals.new_tax_type.fields.type_selector.options.personal_income'
              ),
              value: 'personal_income',
            },
          ],
        },
      ],
    },
    {
      identifier: 'countryCode',
      title: i18n.t('finance.tax_type.datatable.fields.country_code'),
      path: 'countryCode',
      type: FilterType.String,
      categories: [
        {
          kind: 'MultipleChoice',
          operation: 'in',
          options: countries(i18n.locale),
        },
      ],
    },
  ]

  return (
    <DataTable.Provider
      source={source}
      pagination={{ kind: 'offset', itemsPerPage: 25 }}
      sort={{ path: 'name', direction: 'asc' }}
      filters={filters}
    >
      <Box gap="s16">
        <DataTable.HeadBar
          disableDefaultActions
          actions={
            <Button iconName="add" type="secondary" onClick={onNewTaxTypeClick}>
              {i18n.t('finance.settings.accounting.tax_type.add_tax_type')}
            </Button>
          }
          search={{
            placeholder: i18n.t('common.search'),
            value: search,
            onChange: onSearch,
          }}
        />
        <DataTable.Table />
      </Box>
    </DataTable.Provider>
  )
}

export default TaxTypeDatatable
